import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Trash2, Plus, ArrowUp, ArrowDown } from "lucide-react";

const ICON_OPTS = [
  "Lightbulb","Wind","Home","Baby","Dog","Package","Motorcycle","Helmet",
  "Wallet","AlertTriangle","Car","Sun","MapPin","Bell","Shield","Heart",
  "Yape","Plin","Bank","MercadoPago","Card","Mail","MessageCircle","Users",
];

const COLOR_OPTS = [
  "from-yellow-500 to-amber-500",
  "from-cyan-500 to-blue-500",
  "from-red-500 to-rose-500",
  "from-pink-500 to-rose-500",
  "from-amber-500 to-orange-500",
  "from-blue-500 to-indigo-500",
  "from-purple-500 to-violet-500",
  "from-green-500 to-emerald-500",
  "from-slate-500 to-slate-600",
  "from-orange-500 to-red-500",
  "from-blue-600 to-blue-700",
  "from-red-600 to-rose-600",
];

// Simple key/value block editor
export const FieldRow = ({ label, value, onChange, testid, multiline = false, hint }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-300 font-medium">{label}</label>
    {multiline ? (
      <Textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-700 border-slate-600 text-white min-h-[80px]"
        data-testid={testid}
      />
    ) : (
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-700 border-slate-600 text-white"
        data-testid={testid}
      />
    )}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
    {typeof value === "string" && value.includes("[COMPLETAR POR ADMIN]") && (
      <p className="text-xs text-amber-400">⚠️ Este campo tiene un placeholder pendiente por completar.</p>
    )}
  </div>
);

// Array CRUD (real_life cases, faqs, payment_methods, features, usos categories)
export const ArrayEditor = ({ items = [], onChange, renderItem, blankItem, testidPrefix, itemLabel = "Elemento" }) => {
  const update = (idx, newVal) => {
    const next = [...items];
    next[idx] = newVal;
    onChange(next);
  };
  const add = () => onChange([...items, JSON.parse(JSON.stringify(blankItem))]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const move = (idx, delta) => {
    const to = idx + delta;
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    [next[idx], next[to]] = [next[to], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <div key={idx} className="bg-slate-900/40 border border-slate-700 rounded-lg p-4" data-testid={`${testidPrefix}-item-${idx}`}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide">{itemLabel} {idx + 1}</p>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white" onClick={() => move(idx, -1)} data-testid={`${testidPrefix}-up-${idx}`} disabled={idx === 0}>
                <ArrowUp className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white" onClick={() => move(idx, 1)} data-testid={`${testidPrefix}-down-${idx}`} disabled={idx === items.length - 1}>
                <ArrowDown className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => remove(idx)} data-testid={`${testidPrefix}-remove-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {renderItem(it, (patch) => update(idx, { ...it, ...patch }))}
        </div>
      ))}
      <Button variant="outline" onClick={add} className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300" data-testid={`${testidPrefix}-add-btn`}>
        <Plus className="w-4 h-4 mr-2" /> Agregar {itemLabel.toLowerCase()}
      </Button>
    </div>
  );
};

export const IconPicker = ({ value, onChange, testid }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-300 font-medium">Ícono</label>
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 bg-slate-700 border border-slate-600 rounded-md text-white text-sm px-3"
      data-testid={testid}
    >
      {ICON_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export const ColorPicker = ({ value, onChange, testid }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-300 font-medium">Color (Tailwind gradient)</label>
    <div className="grid grid-cols-6 gap-1">
      {COLOR_OPTS.map((c) => (
        <button
          type="button"
          key={c}
          onClick={() => onChange(c)}
          className={`h-8 rounded bg-gradient-to-br ${c} ${value === c ? "ring-2 ring-white" : "opacity-70 hover:opacity-100"}`}
          title={c}
          data-testid={`${testid}-${c.split(" ")[0].replace("from-", "")}`}
        />
      ))}
    </div>
  </div>
);

export { ICON_OPTS, COLOR_OPTS };
